import { format } from "date-fns";
import React, { useState } from "react";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { ButtonIcon, EmptyState, Pagination, PanelBase } from "@performyard/py-components";
import ComponentLoader from "@performyard/py-components/src/components/atoms/ComponentLoader";
import type {
	AssignmentHistoryEntryJSON,
	CompetencyDictEntry,
	Level,
	ListAssignmentHistoryResponseJSON,
} from "@performyard/service-contracts";

import { useAssignmentHistory } from "../../../../api/hooks/defs/competency-assignments";

import { NOT_ASSESSED_LABEL, resolveLevelName } from "../../utils";

import PanelHeaderCard from "../PanelHeaderCard";

type ViewHistoryPanelProps = {
	isOpen: boolean;
	onClose: () => void;
	assignmentId: string;
	competency: CompetencyDictEntry | undefined;
	assignee?: { _id: string; name: string };
};

const buildEventSentence = (
	entry: AssignmentHistoryEntryJSON,
	data: ListAssignmentHistoryResponseJSON,
	levels: Level[] | undefined
): React.ReactElement => {
	const actor = data.users[entry.created.user_id]?.name ?? "Unknown user";
	const actorEl = <strong className="font-semibold">{actor}</strong>;
	const fmtLevel = (rank: number | null): string => resolveLevelName(levels, rank) ?? NOT_ASSESSED_LABEL;

	switch (entry.event_type) {
		case "level_update":
			return (
				<span>
					{actorEl} updated the proficiency level for this competency to {fmtLevel(entry.new_level)}.
				</span>
			);
		case "expected_level_update":
			return (
				<span>
					{actorEl} updated the expected level for this competency to {fmtLevel(entry.new_level)}.
				</span>
			);
		case "role_assignment":
			return (
				<span>
					{actorEl} assigned this competency through the {data.roles[entry.role_id]?.name ?? "Unknown role"}{" "}
					role.
				</span>
			);
		case "role_unassignment":
			return (
				<span>
					{actorEl} unassigned this competency when leaving the{" "}
					{data.roles[entry.role_id]?.name ?? "Unknown role"} role.
				</span>
			);
		case "role_competency_removal":
			return (
				<span>
					{actorEl} removed this competency from the {data.roles[entry.role_id]?.name ?? "Unknown role"} role.
				</span>
			);
		case "company_wide_assignment":
			return <span>{actorEl} assigned this competency as a company-wide competency.</span>;
		default:
			return entry satisfies never;
	}
};

const HISTORY_PAGE_SIZE = 10;

const ViewHistoryPanel = ({
	isOpen,
	onClose,
	assignmentId,
	competency,
	assignee,
}: ViewHistoryPanelProps): React.ReactElement => {
	// Page state resets via the parent's `key={...assignmentId}` (remount on row change).
	const [pageNumber, setPageNumber] = useState(1);

	const { data, isLoading } = useAssignmentHistory(
		assignmentId,
		{ limit: HISTORY_PAGE_SIZE, skip: (pageNumber - 1) * HISTORY_PAGE_SIZE },
		{
			// Gated on isOpen: the panel stays mounted while closed, so an always-enabled query has
			// no refetch trigger on reopen and would keep serving the pre-change cache.
			enabled: isOpen && !!assignmentId,
			placeholderData: (previousData) => previousData,
		}
	);

	const pageCount = Math.ceil((data?.totalCount ?? 0) / HISTORY_PAGE_SIZE);

	return (
		<PanelBase isOpen={isOpen} onClose={onClose} side="right">
			<div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
				<div className="flex items-start justify-between gap-2">
					<span className="text-paragraph-2 font-regular text-neutral-9">History</span>
					<ButtonIcon
						variant="ghost"
						color="neutral"
						shade={5}
						size={24}
						icon={faXmark}
						onClick={onClose}
						aria-label="Close panel"
					/>
				</div>

				{competency && (
					<PanelHeaderCard
						name={competency.name}
						isCompanyWide={competency.is_company_wide}
						assignee={assignee}
						description={competency.description}
					/>
				)}

				{isLoading ? (
					<div className="flex items-center justify-center py-12">
						<ComponentLoader />
					</div>
				) : !data || data.history.length === 0 ? (
					<div className="flex-1">
						<EmptyState variant="illustration" option={6} label="No history for this competency" />
					</div>
				) : (
					<>
						<div className="flex flex-col">
							{data.history.map((entry, index) => (
								<div
									key={entry._id}
									// Divider between entries: top border on all but the first. Preflight is
									// disabled here, so the unused sides must be zeroed and border-solid set
									// explicitly — same idiom as py-components' Accordion "underline" variant.
									className={`flex flex-col gap-1 pb-3 ${
										index > 0
											? "border-x-0 border-t border-b-0 border-solid border-neutral-2 pt-3"
											: ""
									}`}
								>
									<p className="text-caption text-neutral-5">
										{format(new Date(entry.effective_date), "EEE, MMM d yyyy")}
									</p>
									<p className="text-paragraph-3 [overflow-wrap:anywhere] text-neutral-8">
										{buildEventSentence(entry, data, competency?.levels)}
									</p>
								</div>
							))}
						</div>
						{pageCount > 1 && (
							<div className="mt-2 flex justify-center">
								<Pagination
									currentPage={pageNumber}
									pageCount={pageCount}
									onPageChange={setPageNumber}
								/>
							</div>
						)}
					</>
				)}
			</div>
		</PanelBase>
	);
};

export default ViewHistoryPanel;
